import { useState, useEffect, useMemo } from "react";
import {
  Package,
  User,
  CheckSquare,
  Bell,
  Users,
  Mail,
  FolderTree,
  Code2,
  KeyRound,
} from "lucide-react";

import Layout from "@/components/Layout";
import { PageHeader } from "@/components/PageHeader";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { SubscriptionPlans } from "@/components/Settings/SubscriptionPlans/SubscriptionPlans";
import { ProfileSettings } from "@/components/Settings/ProfileSettings/ProfileSettings";
import { AccountSettings } from "@/components/Settings/AccountSettings/AccountSettings";
import { TeamManagement } from "@/components/Settings/TeamManagement/TeamManagement";
import { TaskSettings } from "@/components/Settings/TaskSettings/TaskSettings";
import { NotificationSettings } from "@/components/Settings/NotificationSettings/NotificationSettings";
import { EmailSettings } from "@/components/Settings/EmailSettings/EmailSettings";
import { DocumentSettings } from "@/components/Settings/DocumentSettings/DocumentSettings";
import { WidgetSettings } from "@/components/Settings/WidgetSettings/WidgetSettings";
import { ApiKeySettings } from "@/components/Settings/ApiKeySettings/ApiKeySettings";
import { SettingsTabErrorBoundary } from "@/components/Settings/SettingsTabErrorBoundary";
import { useAuth } from "@/hooks/use-auth";
import { useSubscriptionStatus } from "@/hooks/use-subscription-guard";

const SETTINGS_TABS = [
  "plans",
  "profile",
  "tasks",
  "documents",
  "widget",
  "api-keys",
  "notifications",
  "users",
  "email",
] as const;

type SettingsTab = (typeof SETTINGS_TABS)[number];

function isValidSettingsTab(value: string | null): value is SettingsTab {
  return value !== null && SETTINGS_TABS.includes(value as SettingsTab);
}

// Tabs that require an active subscription to access
const SUBSCRIPTION_REQUIRED_TABS: SettingsTab[] = ["tasks", "documents", "notifications", "email"];

export default function Settings() {
  // Get authenticated user from auth hook
  const { user } = useAuth();

  // Check subscription status using shared hook with app-wide caching
  const { data: subscriptionStatus, isFetched: isSubscriptionFetched } = useSubscriptionStatus();

  // Subscription state - only consider known after fetch completes
  const isSubscriptionKnown = useMemo(
    () => isSubscriptionFetched || subscriptionStatus !== undefined,
    [isSubscriptionFetched, subscriptionStatus]
  );
  
  const hasActiveSubscription = useMemo(
    () => subscriptionStatus?.hasActiveSubscription === true,
    [subscriptionStatus]
  );

  // Helper to check if a tab requires subscription and user lacks access
  const isTabRestricted = useMemo(
    () => (tab: SettingsTab) => {
      if (!SUBSCRIPTION_REQUIRED_TABS.includes(tab)) return false;
      if (!isSubscriptionKnown) return false; // Don't restrict until we know
      return !hasActiveSubscription;
    },
    [isSubscriptionKnown, hasActiveSubscription]
  );

  // Persist active tab in sessionStorage, but prioritize URL parameter
  const [activeTab, setActiveTab] = useState<SettingsTab>(() => {
    if (typeof window !== "undefined") {
      // Check URL parameter first
      const params = new URLSearchParams(window.location.search);
      const tabParam = params.get("tab");
      
      if (isValidSettingsTab(tabParam)) {
        return tabParam;
      }
      
      // Fall back to sessionStorage or default
      const storedTab = sessionStorage.getItem("settings-active-tab");
      if (isValidSettingsTab(storedTab)) {
        return storedTab;
      }

      return "plans";
    }
    return "plans";
  });

  const isAdmin = user?.role === "admin";

  const handleTabChange = (value: string) => {
    if (!isValidSettingsTab(value)) {
      return;
    }

    const tab = value;
    
    // Block access to restricted tabs
    if (isTabRestricted(tab)) {
      return;
    }
    
    setActiveTab(tab);
    if (typeof window !== "undefined") {
      sessionStorage.setItem("settings-active-tab", tab);
    }
  };

  // Redirect to plans tab if user loses subscription access while on a restricted tab
  useEffect(() => {
    if (isTabRestricted(activeTab)) {
      setActiveTab("plans");
    }
  }, [activeTab, isTabRestricted]);

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 space-y-6">
        <PageHeader
          title="Settings"
          description="Manage your account, team, and system preferences"
        />

        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className="space-y-6"
        >
          <TabsList className="grid w-full grid-cols-9 gap-2 h-auto p-2 max-sm:grid-cols-2 max-sm:gap-2 max-sm:p-2">

            <TabsTrigger value="plans" className="flex items-center gap-2 max-sm:justify-center max-sm:px-3 max-sm:py-3 max-sm:text-sm">
              <Package className="h-4 w-4 max-sm:h-5 max-sm:w-5" />
              <span>Plans</span>
            </TabsTrigger>

            <TabsTrigger 
              value="profile" 
              className="flex items-center gap-2 max-sm:justify-center max-sm:px-3 max-sm:py-3 max-sm:text-sm"
            >
              <User className="h-4 w-4 max-sm:h-5 max-sm:w-5" />
              <span>Profile</span>
            </TabsTrigger>

            <TabsTrigger 
              value="tasks" 
              className="flex items-center gap-2 max-sm:justify-center max-sm:px-3 max-sm:py-3 max-sm:text-sm"
              disabled={isTabRestricted("tasks")}
            >
              <CheckSquare className="h-4 w-4 max-sm:h-5 max-sm:w-5" />
              <span>Workflows</span>
            </TabsTrigger>

            <TabsTrigger 
              value="documents" 
              className="flex items-center gap-2 max-sm:justify-center max-sm:px-3 max-sm:py-3 max-sm:text-sm"
              disabled={isTabRestricted("documents")}
            >
              <FolderTree className="h-4 w-4 max-sm:h-5 max-sm:w-5" />
              <span>Folders</span>
            </TabsTrigger>

            <TabsTrigger
              value="widget"
              className="flex items-center gap-2 max-sm:justify-center max-sm:px-3 max-sm:py-3 max-sm:text-sm"
            >
              <Code2 className="h-4 w-4 max-sm:h-5 max-sm:w-5" />
              <span>Embed</span>
            </TabsTrigger>

            <TabsTrigger
              value="api-keys"
              className="flex items-center gap-2 max-sm:justify-center max-sm:px-3 max-sm:py-3 max-sm:text-sm"
            >
              <KeyRound className="h-4 w-4 max-sm:h-5 max-sm:w-5" />
              <span>API Keys</span>
            </TabsTrigger>

            <TabsTrigger 
              value="notifications" 
              className="flex items-center gap-2 max-sm:justify-center max-sm:px-3 max-sm:py-3 max-sm:text-sm"
              disabled={isTabRestricted("notifications")}
            >
              <Bell className="h-4 w-4 max-sm:h-5 max-sm:w-5" />
              <span>Notifications</span>
            </TabsTrigger>

            <TabsTrigger 
              value="email" 
              className="flex items-center gap-2 max-sm:justify-center max-sm:px-3 max-sm:py-3 max-sm:text-sm"
              disabled={isTabRestricted("email")}
            >
              <Mail className="h-4 w-4 max-sm:h-5 max-sm:w-5" />
              <span>Email</span>
            </TabsTrigger>

            <TabsTrigger 
              value="users" 
              className="flex items-center gap-2 max-sm:justify-center max-sm:px-3 max-sm:py-3 max-sm:text-sm"
            >
              <Users className="h-4 w-4 max-sm:h-5 max-sm:w-5" />
              <span>Team</span>
              {isAdmin && (
                <Badge variant="secondary" className="ml-1 hidden sm:inline-flex">
                  Admin
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="plans">
            <SettingsTabErrorBoundary tabName="Subscription Plans">
              <SubscriptionPlans />
            </SettingsTabErrorBoundary>
          </TabsContent>

          <TabsContent value="profile">
            <div className="space-y-6">
              <SettingsTabErrorBoundary tabName="Profile Settings">
                <ProfileSettings />
              </SettingsTabErrorBoundary>
              <SettingsTabErrorBoundary tabName="Account Settings">
                <AccountSettings />
              </SettingsTabErrorBoundary>
            </div>
          </TabsContent>

          <TabsContent value="tasks">
            <SettingsTabErrorBoundary tabName="Task Settings">
              <TaskSettings />
            </SettingsTabErrorBoundary>
          </TabsContent>

          <TabsContent value="documents">
            <SettingsTabErrorBoundary tabName="Document Settings">
              <DocumentSettings />
            </SettingsTabErrorBoundary>
          </TabsContent>

          <TabsContent value="widget">
            <SettingsTabErrorBoundary tabName="Embed Settings">
              <WidgetSettings />
            </SettingsTabErrorBoundary>
          </TabsContent>

          <TabsContent value="api-keys">
            <SettingsTabErrorBoundary tabName="API Key Settings">
              <ApiKeySettings />
            </SettingsTabErrorBoundary>
          </TabsContent>

          <TabsContent value="notifications">
            <SettingsTabErrorBoundary tabName="Notification Settings">
              <NotificationSettings />
            </SettingsTabErrorBoundary>
          </TabsContent>

          <TabsContent value="users">
            <SettingsTabErrorBoundary tabName="Team Management">
              <TeamManagement />
            </SettingsTabErrorBoundary>
          </TabsContent>

          <TabsContent value="email">
            <SettingsTabErrorBoundary tabName="Email Settings">
              <EmailSettings />
            </SettingsTabErrorBoundary>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

